import { autoinject, bindable, observable } from 'aurelia-framework';
import { ServiceStructureService, ServiceNodeModel } from '../service-structure-service';
import { LinkedMappingsService, ITargetToServiceAM } from './linked-mappings-service';
import { OrganizationalUnitService, OrganizationalNodeModel } from '../../customers/organizational-units/organizational-unit-service';
import { MappingDraggableHandler, MappingDroppableHandler, MappingDraggableModel } from './mapping-interaction-handler';
import { NodeModel } from '../../../models/node-model';
import { json } from 'aurelia-fetch-client';
import { FiscalYearService } from '../../customers/fiscal-year-service';
import * as moment from 'moment';
import { AccountingDimensionService } from '../../accounting-dimensions/accounting-dimension-api';
import { INameIdAM } from '../../../models/NameIdAM';
import { NotificationType, ResourcedNotificationService } from '../../../components/notifications/notification-service';

export interface IDimensionValue {
	id: string;
	identifier: string;
	name: string;
}

export interface IDimension {
	id: string;
	name: string;
	values: IDimensionValue[];
}

@autoinject
export class ServiceTargeting {

	@bindable customerId: string;
	@bindable fiscalYearId: string;
	@bindable serviceStructureId: string;
	@bindable serviceStructureName: string;

	@observable dimensions: IDimension[];

	refreshingServices = true;
	refreshingOrganizations = true;
	draggableHandler: MappingDraggableHandler;
	droppableHandler: MappingDroppableHandler;
	currentYear: number = null;

	isAutomaticTargetingBypassed = false;
	canEditAutomaticTargetingBypass = false;
	savingAutomaticTargetingBypass = false;
	bypassConfirmDialog: HTMLElement;

	get services(): ServiceNodeModel[] {
		return this.serviceStructureService.serviceStructureNodes;
	}

	get organizationalNodeModels(): OrganizationalNodeModel[] {
		return this.organizationalUnitService.treeNodes;
	}

	constructor(
		private serviceStructureService: ServiceStructureService,
		private organizationalUnitService: OrganizationalUnitService,
		private linkedMappingsService: LinkedMappingsService,
		private accountingDimensionService: AccountingDimensionService,
		private fiscalYearApi: FiscalYearService,
		private notificationService: ResourcedNotificationService) {

		this.draggableHandler = new MappingDraggableHandler();
		this.droppableHandler = new MappingDroppableHandler(this.updateMappings.bind(this));
	}

	async bind() {
		this.dimensions = (await this.accountingDimensionService.getDimensionsForCustomer(this.customerId))
			.map(d => d as IDimension);
	}

	async updateMappings(serviceId: string, draggedNode: MappingDraggableModel) {
		const dto: ITargetToServiceAM = {
			serviceId: serviceId,
			costCenterId: null,
			serviceTargetingId: null
		};
		
		if (draggedNode.targetingModel) {
			dto.serviceTargetingId = draggedNode.targetingModel.id;
			dto.costCenterId = draggedNode.targetingModel.costCenterId;
		} else {
			dto.costCenterId = draggedNode.node.id;
		}
		await this.linkedMappingsService.updateMappings(this.fiscalYearId, this.serviceStructureId, [dto]);

		this.linkedMappingsService.refreshLinksCache(this.fiscalYearId, this.serviceStructureId, true);
	}

	async toggleIsIgnored(node: OrganizationalNodeModel) {
		if (node.nodeType != "CostCenter") { return; }
		node.toggleCssClass('ignored');
		await this.linkedMappingsService.setCostCenterIsIgnoredStatus(node.id, node.isIgnored);
	}

	async refreshAutomaticTargetingBypass() {
		const state = await this.linkedMappingsService.getAutomaticTargetingBypass(this.fiscalYearId, this.serviceStructureId);
		if (!state) {
			// State unknown: the button stays disabled
			this.notificationService.push('ServiceTargeting_Index_Error_BypassAutomaticTargetingLoadFailed', NotificationType.Error);
			return;
		}
		this.isAutomaticTargetingBypassed = state.isBypassed;
		this.canEditAutomaticTargetingBypass = state.canEdit;
	}

	// Independent of the ignore selections of the cost centers and of hideIgnored.
	// Switching on is confirmed in a dialog, switching off is saved directly.
	async toggleAutomaticTargetingBypass() {
		if (!this.canEditAutomaticTargetingBypass || this.savingAutomaticTargetingBypass) { return; }

		if (!this.isAutomaticTargetingBypassed) {
			// Same workaround as in report-word-export-dialog (TS6385: HTMLDialogElement is deprecated)
			(this.bypassConfirmDialog as any).showModal();
			return;
		}
		await this.saveAutomaticTargetingBypass(false);
	}

	async confirmAutomaticTargetingBypass() {
		this.closeBypassConfirmDialog();
		await this.saveAutomaticTargetingBypass(true);
	}

	closeBypassConfirmDialog() {
		(this.bypassConfirmDialog as any).close();
	}

	private async saveAutomaticTargetingBypass(bypass: boolean) {
		if (!this.canEditAutomaticTargetingBypass || this.savingAutomaticTargetingBypass) { return; }

		this.savingAutomaticTargetingBypass = true;
		try {
			const state = await this.linkedMappingsService.setAutomaticTargetingBypass(this.fiscalYearId, this.serviceStructureId, bypass);
			if (!state) {
				// Not saved: keep showing the previous state
				this.notificationService.push('ServiceTargeting_Index_Error_BypassAutomaticTargetingSaveFailed', NotificationType.Error);
				return;
			}
			this.isAutomaticTargetingBypassed = state.isBypassed;
		}
		finally {
			this.savingAutomaticTargetingBypass = false;
		}
	}

	refreshTrees(): void {

		const refreshServices = this.serviceStructureService.refreshServicesSelectedToFiscalYear(this.fiscalYearId, this.serviceStructureId);
		const refreshOrganizationalUnits = this.organizationalUnitService.refreshOrganizationalUnitsForFiscalYear(this.fiscalYearId, true);

		refreshServices.then(() => this.refreshingServices = false);
		refreshOrganizationalUnits.then(async () => {
			await this.linkedMappingsService.refreshCostCenterConditionsCache(this.fiscalYearId, this.serviceStructureId);
			this.refreshingOrganizations = false;
		});

		Promise.all([refreshServices, refreshOrganizationalUnits]).then(() =>
			this.linkedMappingsService.refreshLinksCache(this.fiscalYearId, this.serviceStructureId, true)
		);
	}

	getStartYear() {
		this.fiscalYearApi.getFiscalYear(this.fiscalYearId).then(f => {
			this.currentYear = moment(f.startDate).year();
		});
	}

	attached() {
		this.getStartYear();
		this.refreshTrees();
		this.refreshAutomaticTargetingBypass();
	}
}
